import cls from 'classnames';
import React, { useEffect, useState } from 'react';

import Icon from '@/atoms/Icon/Icon';
import Image from './Image';

import {
  getVideoClass,
  getCloudinaryVideo,
  getCloudinaryVideoPoster,
  CLOUDINARY_VIDEO_TRANSFORMATIONS,
} from '../Media.constants';
import { isDDEnv, isWHEnv } from '@/utils/helper';
import Text from '@/atoms/Text/Text';

// 1. Basic Video Component
export function Video({
  lightbox,
  wrapperClassname,
  sources,
  videoProps: {
    style,
    poster,
    controls,
    autoPlay,
    size: { height } = {},
    loop,
    muted,
    playsInline,
  } = {},
}) {
  const videoClass = getVideoClass({ style });
  const isDD = isDDEnv();
  const videoClassDD = videoClass + " h-[764px] md:h-[840px]"
  return (
    <div
      className={cls('aaco-media aaco-media--video', {
        'aspect-video': lightbox,
        'h-full w-full': !lightbox,
        [wrapperClassname]: true,
      })}
      style={{ height }}
    >
      <video
        autoPlay={autoPlay}
        className={isDD ? videoClassDD : videoClass}
        controls={controls}
        loop={loop}
        muted={muted}
        onMouseEnter={(e) => e.target.play()}
        playsInline={playsInline}
        poster={poster}
      >
        {sources &&
          sources.length > 0 &&
          sources.map(({ src, contentType }) => (
            <source key={src} src={src} type={contentType} />
          ))}
      </video>
    </div>
  );
}

export default Video;

// 2. Video with Lightbox
export function VideoLightbox({
  videoUrl,
  style,
  title,
  poster,
  content,
  container,
}) {
  const isDD = isDDEnv();
  const isWH = isWHEnv();

  const [lightbox, setLightbox] = useState(false);
  const videoClass = getVideoClass({ style });
  const closeLightbox = () => setLightbox(false);
  const openLightbox = () => setLightbox(true);

  useEffect(() => {
    const body = document?.querySelector('body');
    if (body?.style?.overflow) {
      body.style.overflow = lightbox ? 'hidden' : 'auto';
    }
  }, [lightbox]);

  return (
    <div className="aaco-media aaco-media--video-lightbox h-full w-full relative">
      <div className={videoClass}>
        <Image url={poster} title={title} container={container} />
        <div
          className="aaco-media--video-lightbox__overlay flex items-center justify-center text-white bg-transparent cursor-pointer z-[90] md:top-0 left-0 w-full md:h-full"
          onClick={openLightbox}
        >
          <Text
            tag="button"
            style={cls({
              'aaco-cta__primary-wh p-2 text-white !border-white absolute left-6 md:left-11 bottom-10': isWH,
            })}
          >
            Play Video
          </Text>
        </div>
      </div>

      {/* START: lightbox content */}
      <div className={lightbox ? 'block' : 'hidden'}>
        {!content ? (
          <div
            className={cls(
              'aaco-media--video-lightbox-container',
              'relative h-full w-full'
            )}
          >
            <Video
              lightbox
              sources={[{ src: videoUrl, contentType: 'video/mp4' }]}
              videoProps={{
                controls: true,
              }}
            />
          </div>
        ) : (
          <div
            className={cls(
              'aaco-media--lightbox',
              'fixed left-0 top-0 w-full h-full',
              'z-[100] backdrop-blur-sm'
            )}
          >
            <div
              className={cls(
                'aaco-media--lightbox-bg',
                'fixed w-full h-full top-0 left-0',
                'bg-black opacity-40 z-[110]'
              )}
              onClick={closeLightbox}
            />
            <div
              className="aaco-media--video-lightbox-close fixed top-2 right-10 z-[120] cursor-pointer text-body-large text-white"
              onClick={closeLightbox}
            >
              x
            </div>
            <div className="fixed top-[50%] translate-y-[-50%] left-[48px] md:left-[50%] translate-x-[-24px] md:translate-x-[-50%] z-[120] w-fit shadow-[0_35px_100px_0px_rgba(0,0,0,1)]">
              {content}
            </div>
          </div>
        )}
      </div>
      {/* END: lightbox content */}
    </div>
  );
}
// 3. Cloudinary Video based on Basic Video Component
export function CloudinaryVideo({
  lightbox,
  high = false,
  videoProps,
  asset,
  vertical,
}) {
  const poster = getCloudinaryVideoPoster(asset);
  return (
    <>
      {vertical ? (
        <>
          <Video
            wrapperClassname="block md:hidden"
            videoProps={{ ...videoProps, poster }}
            lightbox={lightbox}
            sources={vertical ? getVideoSourcesVertical(asset) : null}
          />
          <Video
            wrapperClassname="hidden md:block"
            videoProps={{ ...videoProps, poster }}
            lightbox={lightbox}
            sources={getVideoSourcesHigh(asset, high)}
          />
        </>
      ) : (
        <Video
          // wrapperClassname="block md:block"
          videoProps={{ ...videoProps, poster }}
          lightbox={lightbox}
          sources={getVideoSourcesHigh(asset, high)}
        />
      )}
    </>
  );
}

// get video sources based on video type and quality
function getVideoSourcesVertical(asset) {
  return [
    {
      src: getCloudinaryVideo(
        asset,
        'webm',
        CLOUDINARY_VIDEO_TRANSFORMATIONS.vertical_vp9
      ),
      type: 'video/webm',
    },
    {
      src: getCloudinaryVideo(
        asset,
        'mp4',
        CLOUDINARY_VIDEO_TRANSFORMATIONS.vertical_hevc
      ),
      type: "video/mp4; codecs='hevc'",
    },
    {
      src: getCloudinaryVideo(
        asset,
        'mp4',
        CLOUDINARY_VIDEO_TRANSFORMATIONS.vertical_mp4
      ),
      type: 'video/mp4',
    },
  ];
}
function getVideoSourcesHigh(asset, high) {
  return [
    {
      src: getCloudinaryVideo(
        asset,
        'webm',
        CLOUDINARY_VIDEO_TRANSFORMATIONS[high ? 'vp9_full' : 'vp9_half']
      ),
      type: 'video/webm',
    },
    {
      src: getCloudinaryVideo(
        asset,
        'mp4',
        CLOUDINARY_VIDEO_TRANSFORMATIONS[high ? 'hevc_full' : 'hevc_half']
      ),
      type: "video/mp4; codecs='hevc'",
    },
    {
      src: getCloudinaryVideo(
        asset,
        'mp4',
        CLOUDINARY_VIDEO_TRANSFORMATIONS.mp4_low
      ),
      type: 'video/mp4',
    },
  ];
}
